<template>
  <div class="container">
    <!-- Search and Filter Section -->
    <div class="row text-light pb-3 pt-2 mt-1">
      <div class="col-md-6 mb-2">
        <form
          class="search-form search-form-theme2 w-100"
          @submit.prevent
        >
          <img
            src="@/assets/images/skeleton/search.png"
            class="fas fa-search search-icon"
            alt="search"
          >
          <input
            v-model="filter.keyword"
            type="text"
            placeholder="Search in Users"
            name="Search"
            class="search-input"
          >
        </form>
      </div>
      <div class="col-md-3 mb-2">
        <v-select
          v-model="filter.institution_id"
          :options="institutions"
          :reduce="(val) => val.id"
          label="name"
          placeholder="Filter by Institution"
          class="institution-select"
          :clearable="true"
        />
      </div>
      <div class="col-md-3 mb-2">
        <v-select
          v-model="filter.status"
          :options="[{label: 'Active', value: 1}, {label: 'Inactive', value: 0}]"
          :reduce="(val) => val.value"
          label="label"
          placeholder="Filter by Status"
          class="institution-select"
          :clearable="false"
        />
      </div>

    </div>

    <!-- Loading Spinner -->
    <center v-if="isLoading">
      <b-spinner
        variant="primary"
        label="Loading"
      />
    </center>

    <!-- Users Table -->
    <b-table
      v-if="!isLoading && users.data && users.data.length > 0"
      id="drag-index"
      :items="users.data"
      :fields="fields"
      striped
      responsive
      class="users"
      no-local-sorting
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      @sort-changed="onSortChanged"
    >
      <template #cell(fname)="data">
        <b-link
          v-if="data.item.fname != null"
          :to="{ name: 'pages_profile', params: { user_id: data.item.id } }"
          class="text-primary font-weight-bold"
        >
          {{ data.item.fname }} {{ data.item.lname }}
        </b-link>
      </template>

      <template #cell(email)="data">
        <div>{{ data.item.email || "N/A" }}</div>
      </template>

      <template #cell(institution)="data">
        <b-link
          v-if="data.item && data.item.institution && data.item.institution.id"
          :to="{ name: 'institution_profile', params: { id: data.item.institution.id } }"
          class="text-primary font-weight-bold"
        >
          {{ data.item.institution.name }}
        </b-link>
        <div v-else>
          {{
            data.item && data.item.institution
              ? data.item.institution.name
              : "N/A"
          }}
        </div>
      </template>

      <template #cell(consumed_tokens)="data">
        <div>
          {{
            data.item.consumed_tokens !== undefined && data.item.consumed_tokens !== null
              ? data.item.consumed_tokens
              : (data.item.status_counts && data.item.status_counts.consumed_tokens !== undefined && data.item.status_counts.consumed_tokens !== null
                ? data.item.status_counts.consumed_tokens
                : 0)
          }}
        </div>
      </template>

      <template #cell(roles)="data">
        <b-badge
          v-for="value in data.item.roles"
          pill
          variant="light-primary"
        >
          {{ value }}
        </b-badge>
      </template>

      <template #cell(Actions)="data">
        <div class="actions d-flex align-items-center">
          <feather-icon
            v-if="filter.status === 1 && hasPermission('Delete User')"
            v-b-tooltip.hover.top="'Delete User'"
            icon="Trash2Icon"
            size="20"
            class="text-white-50 mr-2 cursor-pointer"
            @click="deleteUser(data.item.id)"
          />
          <feather-icon
            v-if="filter.status === 0 && hasPermission('Delete User')"
            v-b-tooltip.hover.top="'Restore User'"
            icon="RotateCcwIcon"
            size="20"
            class="text-white-50 mr-2 cursor-pointer"
            @click="restoreUser(data.item.id)"
          />
          <feather-icon
            v-if="hasPermission('Assign Role User')"
            v-b-tooltip.hover.top="'Assign Role'"
            class="text-white-50 mr-2 cursor-pointer"
            icon="KeyIcon"
            size="20"
            @click="assignRole(data.item.id)"
          />
          <b-link
            :to="{ name: 'pages_profile', params: { user_id: data.item.id } }"
          >
            <feather-icon
              v-b-tooltip.hover.top="'Show Profile'"
              icon="ChevronRightIcon"
              size="20"
              class="text-white-50 cursor-pointer"
            />
          </b-link>
        </div>
      </template>
    </b-table>

    <!-- No Results Message -->
    <div
      v-if="!isLoading && (!users.data || users.data.length === 0)"
      class="text-center text-light py-5"
    >
      <p>No users found</p>
    </div>

    <!-- Pagination & Per Page -->
    <b-row class="mt-2 w-100 align-items-center" v-if="users.meta">
      <b-col md="4" class="text-light d-flex align-items-center" style="font-size: 0.95rem;">
        <span v-if="users.meta.total > 0" class="d-flex align-items-center" style="margin-left: 2px;">
          Showing 
          <b-form-select
            v-model="filter.per_page"
            :options="[10, 25, 50, 100]"
            class="custom-per-page mx-1"
            @change="onPageChange(1)"
          />
        </span>
      </b-col>
      <b-col md="4" class="d-flex justify-content-center">
        <b-pagination
          v-model="users.meta.current_page"
          :total-rows="users.meta.total"
          :per-page="users.meta.per_page"
          @change="onPageChange"
          class="mb-0"
        />
      </b-col>
      <b-col md="4"></b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BTable,
  BButton,
  BFormCheckbox,
  BCard,
  BRow,
  BCol,
  BBadge,
  BAvatar,
  BCardText,
  BLink,
  BSpinner,
  BPagination,
  VBTooltip,
  BFormSelect,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { mapGetters } from 'vuex'

export default {
  components: {
    BTable,
    BButton,
    BFormCheckbox,
    BCard,
    BRow,
    BCol,
    BBadge,
    BAvatar,
    BCardText,
    BLink,
    BSpinner,
    BPagination,
    vSelect,
    BFormSelect,
  },

  computed: {
    ...mapGetters({
      users: 'users/items',
      institutions: 'institutions/lookups',
    }),
    fields() {
      const baseFields = [
        { key: 'fname', label: 'Name', sortable: true },
        { key: 'email', label: 'Email', sortable: true },
        { key: 'institution', label: 'Institution', sortable: true },
      ]
      if (this.hasPermission('Token Usage User')) {
        baseFields.push({ key: 'consumed_tokens', label: 'Consumed Tokens', sortable: true })
      }
      baseFields.push(
        { key: 'roles', label: 'Roles', sortable: false },
        { key: 'Actions', label: 'Actions', thStyle: { width: '10%' } },
      )
      return baseFields
    },
  },

  directives: {
    'b-tooltip': VBTooltip,
  },

  data() {
    return {
      usersDetails: [],
      isLoading: false,
      sortBy: 'created_at',
      sortDesc: true,
      filter: {
        institution_id: this.$route.params.institution_id || null,
        keyword: null,
        status: 1,
        per_page: 10,
      },

    }
  },

  watch: {
    'filter.keyword': function (newVal) {
      // Debounce search
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.init()
      }, 500)
    },
    'filter.institution_id': function (newVal) {
      this.init()
    },
    'filter.status': function (newVal) {
      this.init()
    },
    $route(to, from) {
      if (to.params.institution_id !== from.params.institution_id) {
        this.filter.institution_id = to.params.institution_id || null
        this.init()
      }
    },
  },

  mounted() {
    this.loadInstitutions()
    this.init()
  },

  beforeDestroy() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
  },

  methods: {
    init() {
      this.isLoading = true
      this.$store
        .dispatch('users/users', {
          ...this.filter,
          sort_by: this.sortBy,
          sort_desc: this.sortDesc,
        })
        .then(response => {
          this.isLoading = false
          this.usersDetails = this.users.data.map(user => ({
            id: user.id,
            name: `${user.fname} ${user.lname}`,
            status: Math.random() > 0.5 ? 'online' : 'offline',
            avatar: user.avatar || 'https://via.placeholder.com/32',
          }))
        })
        .catch(e => {
          this.isLoading = false
          this.$swal({
            icon: 'error',
            title: `<h4>Error fetching user details: ${e.message}</h4>`,
            showConfirmButton: true,
            confirmButtonColor: '#7367f0',
            allowOutsideClick: true,
          })
        })
    },

    loadInstitutions() {
      this.$store.dispatch('institutions/institutionLookups')
    },

    onPageChange(page) {
      this.$store.dispatch('users/users', {
        page,
        ...this.filter,
        sort_by: this.sortBy,
        sort_desc: this.sortDesc,
      })
    },

    onSortChanged(ctx) {
      this.sortBy = ctx.sortBy
      this.sortDesc = ctx.sortDesc
      this.init()
    },

    assignRole(item) {
      this.$router.push({ name: 'assign_role', params: { id: item } })
    },

    deleteUser(id) {
      this.$swal({
        title: 'Are you sure?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'No, cancel it!',
        confirmButtonColor: '#7367f0',
      }).then(result => {
        if (result.value) {
          this.$store
            .dispatch('users/remove', id)
            .then(res => {
              this.init()
              this.$swal({
                icon: 'success',
                title: `<h4>${res.message}</h4>`,
                showConfirmButton: true,
                confirmButtonColor: '#7367f0',
                allowOutsideClick: true,
              })
            })
            .catch(e => {
              this.$swal({
                icon: 'error',
                title: `<h4>Error deleting user: ${e.message}</h4>`,
                showConfirmButton: true,
                confirmButtonColor: '#7367f0',
                allowOutsideClick: true,
              })
            })
        }
      })
    },

    restoreUser(id) {
      this.$swal({
        title: 'Are you sure?',
        text: 'You want to restore this user!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, restore it!',
        cancelButtonText: 'No, cancel!',
        confirmButtonColor: '#7367f0',
      }).then(result => {
        if (result.value) {
          this.$store
            .dispatch('users/restore', id)
            .then(res => {
              this.init()
              this.$swal({
                icon: 'success',
                title: `<h4>${res.message}</h4>`,
                showConfirmButton: true,
                confirmButtonColor: '#7367f0',
                allowOutsideClick: true,
              })
            })
            .catch(e => {
              this.$swal({
                icon: 'error',
                title: `<h4>Error restoring user: ${e.message}</h4>`,
                showConfirmButton: true,
                confirmButtonColor: '#7367f0',
                allowOutsideClick: true,
              })
            })
        }
      })
    },
  },
}
</script>

<style scoped lang="scss">
.search-form {
  position: relative;
  width: 100%;
  background-color: transparent;
  border: 1px solid #ffffff75;
  border-left: none;
  border-right: none;
  border-radius: 15px;
  box-shadow: inset 0 2px 5px rgba(0, 0, 0, 0.4);
  padding: 10px 20px 10px 45px;
  transition: box-shadow 0.3s ease;
}

.search-form-theme2 {
  position: relative;
  width: 100%;
  border-left: none;
  border-right: none;
  padding: 10px 20px 10px 45px;
  transition: box-shadow 0.3s ease;
  background-color: #00000040;
  border: 1px solid #ffffff75;
  border-radius: 15px;
  box-shadow: inset 0 2px 5px rgb(0 0 0 / 65%);
  border-top: unset;
}

.search-form:focus-within {
  box-shadow: 0 0 5px rgba(255, 255, 255, 0.2);
}

.search-form input[type="text"],
.search-form input[type="search"] {
  width: 100%;
  background: transparent;
  border: none;
  color: #fff;
  font-size: 16px;
  outline: none;
}

.search-form input::placeholder {
  color: #ffffffa8;
}

.search-form .search-icon {
  position: absolute;
  top: 50%;
  left: 15px;
  transform: translateY(-50%);
  color: #bbb;
  font-size: 16px;
  cursor: pointer;
}

.institution-select {
  ::v-deep .vs__dropdown-toggle {
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    padding: 5px;
  }

  ::v-deep .vs__search,
  ::v-deep .vs__search:focus {
    color: #fff;
  }

  ::v-deep .vs__selected {
    color: #fff;
  }

  ::v-deep .vs__dropdown-menu {
    background: #2c2c2c;
    border: 1px solid rgba(255, 255, 255, 0.2);
  }

  ::v-deep .vs__dropdown-option {
    color: #fff;

    &:hover {
      background: rgba(115, 103, 240, 0.3);
    }
  }
}

.user-table-container {
  max-width: 90%;
  border-radius: 14px;
  overflow: hidden;
  backdrop-filter: blur(18px);
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
}

.table-header {
  background-color: rgba(35, 34, 34, 0.15);
  color: #fff;
  font-size: 0.95rem;
}

.user-table-body {
  max-height: 500px;
  overflow-y: auto;
}

.actions {
  gap: 8px;
}

.cursor-pointer {
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }
}

.custom-per-page {
  width: auto;
  min-width: 50px;
  height: 29px;
  border-radius: 6px;
  background-color: #121a2b !important; /* Non-white background matching dropdowns */
  color: #fff !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  font-size: 0.9rem;
  padding: 0.15rem 2rem 0.15rem 0.5rem;
  
  &:focus {
    box-shadow: 0 0 5px rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.4) !important;
  }
  
  option {
    background-color: #121a2b;
    color: #fff;
  }
}
</style>
